import React, { useEffect, useRef } from 'react';
import { prop } from 'ramda';

import { tagTypeService } from '@core/js/services/tagType.service';
import { useUserSelector } from '@core/js/store/hooks';
import { insertTrackableHTML } from '@core/js/utils/DOM.util';

import { TAG_TYPE_SUN } from '@interlayer/js/components/system/TagManager/sun/constants';
import { runAdaCommand } from '@interlayer/js/custom/hooks/useAdaChat';
import { useFilterByPlayerTags } from '@interlayer/js/custom/hooks/useFilterSegmentation';
import { transformStrToArray } from '@interlayer/js/custom/utils/methods.util';

import { TPlayerTagsTriggersProps, TTriggeredTagsData } from './types';

export const PlayerTagsTriggers: React.FC<TPlayerTagsTriggersProps> = ({ config: { playerTagsTriggers }, tagName }) => {
    const isLoggedIn = useUserSelector(prop('isLoggedIn'));
    const filterByPlayerTags = useFilterByPlayerTags();
    const triggeredTags = useRef<Map<number, TTriggeredTagsData>>(new Map());

    useEffect(() => {
        if (!isLoggedIn) {
            triggeredTags.current.forEach(triggeredTag => {
                triggeredTag.destroyHtml?.();
            });

            triggeredTags.current = new Map();
            return;
        }

        const checkAndRunTriggers = () => {
            playerTagsTriggers.forEach(({ playerTagsConfig, adaCampaignKeys, htmlContent }, index) => {
                const isTag = filterByPlayerTags(playerTagsConfig);
                const isActionByTag = triggeredTags.current.has(index);

                if (!isTag) {
                    if (isActionByTag) {
                        triggeredTags.current.get(index)?.destroyHtml?.();
                        triggeredTags.current.delete(index);
                    }

                    return;
                }

                if (isTag && isActionByTag) {
                    return;
                }

                if (adaCampaignKeys) {
                    transformStrToArray(adaCampaignKeys).forEach(key => {
                        runAdaCommand(key);
                    });
                }

                const triggeredTagsData: TTriggeredTagsData = {};

                if (htmlContent) {
                    // @ts-expect-error: remove after insertTrackableHTML become ts file
                    triggeredTagsData.destroyHtml = insertTrackableHTML({
                        selector: 'body',
                        html: htmlContent,
                        tagName
                    });
                }

                triggeredTags.current.set(index, triggeredTagsData);
            });
        };

        checkAndRunTriggers();
    }, [filterByPlayerTags, isLoggedIn, playerTagsTriggers, tagName]);

    return null;
};

tagTypeService.registerTagType({ [TAG_TYPE_SUN.playerTagsTriggers]: PlayerTagsTriggers });
